import { HttpClient } from "../../client/HttpClient";
import {
  ChallengeAction,
  LivenessCreateRequest,
  LivenessCreateResponse,
  LivenessSubmitResponse,
} from "../../types/liveness";
import { collectDeviceMetadata } from "../../utils/device";
import { DEFAULT_CHALLENGE_ACTIONS, LIVENESS_TIME_LIMIT_SECONDS } from "../../utils/constants";
import { CameraManager } from "../../camera/CameraManager";
import { VideoRecorder } from "../../camera/VideoRecorder";
import { FaceDetectorEngine } from "../../camera/FaceDetector";
import { ActionDetector } from "../../camera/ActionDetector";
import { LivenessUI, LivenessUIColors } from "./LivenessUI";
import { LivenessUploader } from "./uploader";

/**
 * Liveness module — 3-step flow aligned with Adhere backend.
 *
 * Flow:
 *   Step 1: liveness.create()  → POST /v1/sdk/liveness/create/  (multipart)
 *   Step 2: [camera runs, user performs challenge actions]
 *   Step 3: liveness.submit()  → POST /v1/sdk/liveness/submit/  (multipart)
 *
 * After submit, the backend returns status: "processing".
 * Final results are delivered via webhook (liveness.completed event).
 */
export class LivenessModule {
  constructor(private http: HttpClient) { }

  /**
   * Step 1: Create a liveness challenge entry.
   *
   * POST /v1/sdk/liveness/create/
   * Auth: x-access-token: <sessionToken>
   * Content-Type: multipart/form-data
   */
  async create(params: LivenessCreateRequest): Promise<LivenessCreateResponse> {
    const formData = new FormData();
    // W9/B7: when identity_check is present (data verification), the backend
    // no longer requires identifier/identifier_type — identity is already
    // established via identity_check, so the widget omits them entirely
    // instead of sending the user's real ID number/type a second time.
    //
    // Liveness-only (no identity_check AND no document) is the same story —
    // there is no real identifier to send. Without this check, the
    // fallback below would invent a synthetic `ID-<timestamp>` identifier
    // and store it as the check's identifier, implying an ID number was
    // involved when none was — misleading downstream data for a mode that
    // has no ID number at all.
    const hasDocument = !!(params.document || params.document_front || params.id_file);
    if (params.identity_check || (!hasDocument && !params.identifier)) {
      // no-op — nothing real to send
    } else {
      const rawIdentifier = params.identifier || `ID-${Date.now()}`;
      const safeIdentifier = rawIdentifier.length > 20
        ? rawIdentifier.slice(0, 20)
        : rawIdentifier;
      formData.append("identifier", safeIdentifier);
      formData.append("identifier_type", params.identifier_type);
    }
    formData.append("country", params.country);
    formData.append(
      "challenge_actions",
      JSON.stringify(params.challenge_actions)
    );
    if (params.autoshot_file) {
      formData.append("autoshot", params.autoshot_file, "autoshot.jpg");
    }
    
    // Handle document upload - support both legacy and new front/back fields
    if (params.document) {
      formData.append("document", params.document, "document.jpg");
      if (params.document_back) {
        formData.append("document_back", params.document_back, "document_back.jpg");
      }
    } else if (params.document_front) {
      // Legacy support - map to document for backward compatibility
      formData.append("document", params.document_front, "document_front.jpg");
      if (params.document_back) {
        formData.append("document_back", params.document_back, "document_back.jpg");
      }
    } else if (params.id_file) {
      // Legacy support - map to document for backward compatibility
      formData.append("document", params.id_file, "id_document.jpg");
    }
    
    if (params.identity_check) {
      formData.append("identity_check", String(params.identity_check));
    }
    if (params.snapshot_file) {
      formData.append("snapshot", params.snapshot_file, "snapshot.jpg");
    }
    if (params.device_type) {
      formData.append("device_type", params.device_type);
    }
    if (params.os_name) {
      formData.append("os_name", params.os_name);
    }
    if (params.browser_timezone) {
      formData.append("browser_timezone", params.browser_timezone);
    }
    if (params.fingerprint_id) {
      formData.append("fingerprint_id", params.fingerprint_id);
    }

    return this.http.uploadWithSession<LivenessCreateResponse>(
      "/v1/sdk/liveness/create/",
      formData
    );
  }

  /**
   * Step 3: Submit liveness video recording.
   *
   * POST /v1/sdk/liveness/submit/
   * Auth: x-access-token: <sessionToken>
   * Content-Type: multipart/form-data
   *
   * Note: After submission, the session is revoked (single-use).
   * The backend returns status: "processing" — results come via webhook.
   *
   * Pass onProgress for 0–1 upload-fraction callbacks (via LivenessUploader).
   */
  async submit(
    entryId: number,
    videoBlob: Blob,
    snapshotBlob: Blob,
    autoshotBlob?: Blob,
    videoDurationSeconds?: number,
    videoSizeBytes?: number,
    onProgress?: (fraction: number) => void
  ): Promise<LivenessSubmitResponse> {
    const formData = new FormData();
    formData.append("entry", String(entryId));
    formData.append("video", videoBlob, "liveness.webm");
    formData.append("snapshot", snapshotBlob, "snapshot.jpg");
    if (autoshotBlob) {
      formData.append("autoshot", autoshotBlob, "autoshot.jpg");
    }
    if (videoDurationSeconds !== undefined) {
      formData.append("video_duration_seconds", String(videoDurationSeconds));
    }
    if (videoSizeBytes !== undefined) {
      formData.append("video_size_bytes", String(videoSizeBytes));
    }

    if (onProgress) {
      const uploader = new LivenessUploader(this.http);
      return uploader.upload<LivenessSubmitResponse>(
        "/v1/sdk/liveness/submit/",
        formData,
        { onProgress }
      );
    }

    return this.http.uploadWithSession<LivenessSubmitResponse>(
      "/v1/sdk/liveness/submit/",
      formData
    );
  }

  /**
   * Poll the public SDK status endpoint for the final verification result.
   * Safe to call after the session has been revoked (no auth required) —
   * the ocr_* fields below are only populated by the backend when a live
   * session token is still attached (see HttpClient.publicGet), since they
   * carry PII (name, document number, face thumbnail).
   *
   * GET /v1/sdk/liveness/<entryId>/sdk-result/
   */
  async pollResult(entryId: number): Promise<{
    id: number;
    status: string;
    failure_reason: string | null;
    completed_at: string | null;
    ocr_status?: string;
    is_expired?: boolean;
    ocr_quality_failed?: boolean;
    ocr_name?: string | null;
    ocr_document_number?: string | null;
    ocr_expiry_date?: string | null;
    ocr_face_image_url?: string | null;
    ocr_rejection_reason?: string | null;
  }> {
    return (this.http as any).publicGet(`/v1/sdk/liveness/${entryId}/sdk-result/`);
  }

  /**
   * Full liveness check — orchestrates the entire flow:
   *
   * 1. Run action detection loop (camera + MediaPipe) — challenge actions only,
   *    not recorded (fast head/mouth motion produces blurry frames).
   * 2. Create liveness entry on backend (with challenge actions + optional doc)
   * 3. Record a short, clean, post-challenge clip (neutral pose + a natural
   *    blink) — this is what's sent for face comparison in LIVENESS mode.
   * 4. Submit to backend and return response (status: "processing")
   *
   * Backend runs LIVENESS mode (blink/motion check, then its own comparison
   * frame extracted from this same video) for both data and document
   * verification — see FACE_COMPARISON_USER_GUIDE.md.
   *
   * @param container     - DOM element to mount the liveness UI into
   * @param params        - identifier info and optional document image
   * @param actions       - override the challenge_actions tag sent to the backend (default: BLINK)
   * @param existingEntryId - reuse an already-created LivenessEntry (avoids double charge)
   */
  async startCheck(
    container: HTMLElement,
    params: {
      identifier: string;
      identifier_type: string;
      country: string;
      id_file?: File | Blob;
      document?: File | Blob;
      document_front?: File | Blob;
      document_back?: File | Blob;
      identity_check?: number;
      verification_type?: "data_verification" | "document_verification" | "liveness_only";
      // B4: pre-warmed camera stream from the confirmation screen — if provided
      // camera.open() is skipped and this stream is used directly, eliminating
      // the 1–3s camera init delay when the user taps "Yes, continue".
      prewarmedStream?: MediaStream | null;
      // Client branding — falls back to LivenessUI's default blue/green/red
      // if omitted, so callers that don't have a theme still work.
      colors?: Partial<LivenessUIColors>;
    },
    actions?: ChallengeAction[],
    existingEntryId?: number
  ): Promise<LivenessSubmitResponse> {
    const camera = new CameraManager();
    const recorder = new VideoRecorder();
    const faceEngine = new FaceDetectorEngine();
    const ui = new LivenessUI(params.colors);

    // Tracks whether cleanup has already been called (so the finally block
    // doesn't double-destroy when retry takes over).
    let cleanedUp = false;
    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      faceEngine.destroy();
      camera.stop();
      ui.unmount();
    };

    // Rejects the whole flow if the camera stream or recorder dies mid-scan —
    // raced against the detection loop below via Promise.race, otherwise the
    // video just freezes and detection silently spins until the scan timeout.
    let interruptedReject: ((err: Error) => void) | null = null;
    const interrupted = new Promise<never>((_, reject) => {
      interruptedReject = reject;
    });
    camera.onStreamInterrupted(() => {
      interruptedReject?.(
        new Error("Camera connection was lost. Please check your camera and try again.")
      );
    });
    recorder.onError(() => {
      interruptedReject?.(
        new Error("Recording failed unexpectedly. Please try again.")
      );
    });
    interrupted.catch(() => {});

    try {
      // 1. Open camera — reuse pre-warmed stream if available (B4)
      let stream: MediaStream;
      if (params.prewarmedStream && params.prewarmedStream.active) {
        // Stream already open from confirmation screen — skip getUserMedia delay.
        // Hand it to `camera` so cleanup()'s camera.stop() actually releases it —
        // otherwise this stream is untracked by CameraManager and leaks on any
        // failure/timeout path that only calls camera.stop().
        stream = params.prewarmedStream;
        camera.setStream(stream);
      } else {
        stream = await camera.open();
      }

      // 2. Document capture step — skipped if already captured earlier in
      // the flow (SmartComplyFlow's document_capture step) and passed in
      // via document/document_front; id_file is a legacy alias.
      let capturedDocBlob: Blob | undefined =
        (params.id_file || params.document || params.document_front) as Blob | undefined;
      if (!capturedDocBlob && params.verification_type === "document_verification") {
        capturedDocBlob = await ui.mountDocumentCapture(container, stream);
      }

      // 3. Create a temporary video element to capture autoshot
      const tempVideo = document.createElement("video");
      tempVideo.srcObject = stream;
      tempVideo.muted = true;
      tempVideo.playsInline = true;
      await tempVideo.play();

      // Wait for face to be detected before capturing autoshot
      ui.mount(container, { time_limit_seconds: 0, instruction: "Fit your face inside the oval outline" });
      const uiVideo = ui.getVideoElement();
      if (uiVideo) {
        uiVideo.srcObject = stream;
        await uiVideo.play();
      } else {
        tempVideo.style.cssText = "width:100%;border-radius:12px;transform:scaleX(-1);";
        container.appendChild(tempVideo);
      }

      await faceEngine.init();
      ui.updateInstruction("Fit your face inside the oval outline");

      // Wait until face is stably centred.
      // Tolerant: allows up to 2 missed frames before resetting — avoids
      // flickering "Move closer" for a single dropped frame.
      const videoForDetection = uiVideo || tempVideo;
      await new Promise<void>((resolve) => {
        let stableFrames = 0;
        let missedFrames = 0;
        const STABLE_REQUIRED = 4;   // increased from 2 — ensures face is properly locked before autoshot
        const MISS_TOLERANCE   = 2;  // allow up to 2 consecutive misses before reset
        const MAX_WAIT_MS      = 8000;
        const startTime        = performance.now();

        const checkFace = () => {
          if (performance.now() - startTime >= MAX_WAIT_MS) {
            resolve(); // proceed anyway after timeout
            return;
          }

          if (videoForDetection.readyState >= 2) {
            const detection = faceEngine.detect(videoForDetection, performance.now());
            ui.setFaceTracking(detection.faceDetected);
            if (detection.faceDetected) {
              stableFrames++;
              missedFrames = 0;
              ui.updateInstruction("Good — hold that position");
              if (stableFrames >= STABLE_REQUIRED) {
                resolve();
                return;
              }
            } else {
              missedFrames++;
              if (missedFrames > MISS_TOLERANCE) {
                stableFrames = 0; // full reset only after sustained loss
                ui.updateInstruction("Move closer so your whole face fits in the oval");
              }
            }
          }

          requestAnimationFrame(checkFace);
        };
        requestAnimationFrame(checkFace);
      });

      ui.updateInstruction("Getting ready…");
      // Settle delay — gives camera AE/AF time to lock before challenge starts
      await new Promise((r) => setTimeout(r, 500));

      // W9: Autoshot captured AFTER challenge (moved from before create).
      // We take a placeholder-size stub here only to satisfy liveness.create()
      // which currently expects autoshot_file — the real autoshot is captured
      // after all challenge actions complete and is sent on submit instead.
      // The backend ignores the create-time autoshot for face comparison;
      // only the submit-time autoshot is used. Pass undefined here and
      // capture the real one post-challenge below.

      // 4. Create liveness entry on backend (skip if retrying with existing entry)
      const challengeActions = actions || [...DEFAULT_CHALLENGE_ACTIONS];
      let entry: LivenessCreateResponse;

      if (existingEntryId) {
        entry = { id: existingEntryId, challenge_actions: challengeActions, status: "pending", client_id: "", expires_at: null };
      } else {
        const deviceMeta = collectDeviceMetadata();
        entry = await this.create({
          identifier: params.identifier,
          identifier_type: params.identifier_type,
          country: params.country,
          challenge_actions: challengeActions,
          // W9: No autoshot at create time — real one captured post-challenge
          autoshot_file: undefined,
          document: params.document || params.document_front || params.id_file || capturedDocBlob,
          document_back: params.document_back,
          identity_check: params.identity_check,
          device_type: deviceMeta.device_type,
          os_name: deviceMeta.os_name,
          browser_timezone: deviceMeta.browser_timezone,
          fingerprint_id: deviceMeta.fingerprint_id,
        });
      }

      tempVideo.pause();
      // B3: Remove tempVideo from DOM to release the camera light indicator
      // on devices that show it while a video element holds a stream reference.
      tempVideo.srcObject = null;
      tempVideo.remove();

      // 5. Mount the passive scan UI — single oval view, one instruction,
      // no step-by-step challenge list. The backend still receives a
      // challenge_actions tag (["BLINK"]) for compatibility; the widget no
      // longer commands discrete actions in sequence.
      const uiChallenge = {
        time_limit_seconds: LIVENESS_TIME_LIMIT_SECONDS,
        instruction: "Blink naturally and slowly turn your head from side to side",
      };

      ui.unmount();
      const videoEl = ui.mount(container, { ...uiChallenge, instruction: "Starting scan…" });
      videoEl.srcObject = stream;
      await videoEl.play();

      // 7. Wait for camera AE/AF to re-stabilise after re-mounting the video
      // element before scanning/recording — avoids a blurry first second.
      // "Starting scan…" (set above) covers this wait so the pause between
      // phase 1 (centering) and phase 2 (the actual scan) reads as an
      // intentional handoff instead of the same instruction just sitting
      // there through an unexplained delay.
      await new Promise((r) => setTimeout(r, 1000));
      ui.updateInstruction(uiChallenge.instruction);

      // 8. Start recording immediately — the whole scan window is short and
      // clean by design (no fast head-turn/mouth-open motion to blur it), so
      // it doubles as the clip sent to SmartComply's LIVENESS pipeline.
      recorder.start(stream);
      // Reset the progress ring's clock to now — it was mounted ~1s ago
      // (see step 5/7 above), so without this the ring would visually run
      // ahead of the actual detection window it's supposed to represent.
      ui.startScanClock();

      // Passive natural-motion detection: watch for a genuine blink or head
      // movement (whichever the user does first while looking around), for
      // up to the scan time limit. A minimum dwell time avoids ending the
      // scan on a single lucky frame before the camera has settled.
      const MIN_SCAN_MS = 2000;
      const actionDetector = new ActionDetector();

      let scanAnimFrameId: number | undefined;
      let scanTimeoutId: ReturnType<typeof setTimeout> | undefined;
      const scanPromise = new Promise<"completed" | "timeout">(
        (resolve) => {
          const scanStart = performance.now();

          scanTimeoutId = setTimeout(() => {
            if (scanAnimFrameId !== undefined) cancelAnimationFrame(scanAnimFrameId);
            resolve("timeout");
          }, uiChallenge.time_limit_seconds * 1000);

          const detectLoop = () => {
            if (videoEl.readyState >= 2) {
              const detection = faceEngine.detect(videoEl, performance.now());
              const scan = actionDetector.check(detection);
              ui.updateScanProgress(scan.livenessConfirmed);
              ui.setFaceTracking(detection.faceDetected);

              if (!detection.faceDetected) {
                ui.updateInstruction("Move closer so your whole face fits in the oval");
              } else {
                ui.updateInstruction(uiChallenge.instruction);
              }

              // Require the face to be present in THIS frame too — scan.livenessConfirmed
              // latches true forever once a blink/motion is seen, so without this check
              // the scan could complete on elapsed time alone while the user has since
              // moved out of frame, ending the recording on an empty/unusable frame
              // right before the autoshot capture below.
              const elapsed = performance.now() - scanStart;
              if (detection.faceDetected && scan.livenessConfirmed && elapsed >= MIN_SCAN_MS) {
                clearTimeout(scanTimeoutId);
                if (scanAnimFrameId !== undefined) cancelAnimationFrame(scanAnimFrameId);
                resolve("completed");
                return;
              }
            }

            scanAnimFrameId = requestAnimationFrame(detectLoop);
          };

          detectLoop();
        }
      );

      let detectionResult: "completed" | "timeout";
      try {
        detectionResult = await Promise.race([scanPromise, interrupted]);
      } catch (interruptErr) {
        // Camera died mid-recording — stop the loop/timer and discard the
        // now-unusable (truncated/frozen) recording rather than submitting it.
        if (scanAnimFrameId !== undefined) cancelAnimationFrame(scanAnimFrameId);
        if (scanTimeoutId !== undefined) clearTimeout(scanTimeoutId);
        recorder.stop().catch(() => {});
        throw interruptErr;
      }

      // ── On timeout: show retry screen, clean up, restart ──────────
      if (detectionResult === "timeout") {
        // IMPORTANT: mark cleanedUp=true BEFORE returning the new Promise so
        // the outer `finally` block does NOT call cleanup() immediately —
        // which would destroy the UI before the user sees the timeout screen.
        cleanedUp = true;

        return new Promise<LivenessSubmitResponse>((resolveRetry, rejectRetry) => {
          ui.showTimeout(() => {
            // User clicked "Try Again" — now it is safe to destroy this attempt
            recorder.stop().catch(() => {}); // discard partial recording
            faceEngine.destroy();
            camera.stop();
            ui.unmount();

            // Restart liveness with same entry ID to prevent duplicate charges (D1)
            // Pass capturedDocBlob forward so document scan step is not repeated.
            this.startCheck(
              container,
              { ...params, id_file: capturedDocBlob },
              actions,
              entry.id
            )
              .then(resolveRetry)
              .catch(rejectRetry);
          });
          // showTimeout waits for the user to click — the promise stays pending.
        });
      }

      // ── On completion: capture stills, close out the recording, submit ──

      ui.updateInstruction("Verifying...");

      // Capture autoshot (real selfie) — face already settled from the scan,
      // camera AE locked. Same quality-gated capture used throughout.
      const autoshotBlob = await this.captureFrame(videoEl);
      if (!autoshotBlob || autoshotBlob.size === 0) {
        recorder.stop().catch(() => {});
        throw new Error("Failed to capture selfie. Please ensure your face is visible and try again.");
      }

      // Capture a snapshot frame as well (same timing, slightly different use)
      const snapshotBlob = await this.captureFrame(videoEl);

      // 9. Stop recording — the scan window itself (short, clean, natural
      // blink/motion) is what SmartComply's LIVENESS mode analyses: blink
      // and motion detection, then its own comparison frame extracted from
      // this same video.
      const recorded = await recorder.stop();
      const videoBlob = recorded.blob;
      const durationSeconds = recorded.durationSeconds;

      // 10. Submit to backend — always include autoshot so document-flow
      // entries (which bypassed create() via existingEntryId) still get it.
      const submitResult = await this.submit(
        entry.id,
        videoBlob,
        snapshotBlob,
        autoshotBlob,
        durationSeconds,
        videoBlob.size,
        (fraction) => {
          const pct = Math.round(fraction * 100);
          ui.updateInstruction(pct < 100 ? `Uploading... ${pct}%` : "Verifying...");
        }
      );

      // 11. Show success overlay briefly before the flow advances
      await ui.showResult(
        submitResult.status === "failed" ? "failed" : "processing",
        submitResult.id
      );

      return submitResult;

    } finally {
      cleanup();
    }
  }

  /**
   * Capture a single sharp frame from a video element as a JPEG Blob.
   * Retries up to maxAttempts times (300 ms apart) if the Laplacian blur
   * score is below threshold, then falls back to the best frame captured.
   *
   * Threshold calibration:
   *   The server rejects images with OpenCV Laplacian variance < 80.
   *   This SDK uses sum-of-squared Laplacian (different normalisation) —
   *   empirically ~100 here corresponds to ~80 server-side. We aim for 120
   *   to give a comfortable safety margin.
   */
  private async captureFrame(
    video: HTMLVideoElement,
    maxAttempts = 5
  ): Promise<Blob> {
    const MIN_BLUR_SCORE       = 120;  // calibrated to server threshold of 80 (different scale)
    const MIN_BRIGHTNESS       = 40;   // 0-255 — below this = too dark for face comparison
    const MAX_BRIGHTNESS       = 220;  // above this = too bright/overexposed
    const RETRY_DELAY_MS       = 200;
    let bestBlob: Blob | null  = null;
    let bestScore              = -1;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const canvas = document.createElement("canvas");
      canvas.width  = video.videoWidth  || 640;
      canvas.height = video.videoHeight || 480;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Failed to get canvas context");
      ctx.drawImage(video, 0, 0);

      const blurScore   = this._computeBlurScore(canvas);
      const brightness  = this._computeBrightness(canvas);
      const brightnessOk = brightness >= MIN_BRIGHTNESS && brightness <= MAX_BRIGHTNESS;

      // Combined quality score — blur is primary, brightness gates it
      const qualityScore = brightnessOk ? blurScore : 0;

      const blob = await new Promise<Blob | null>((res) =>
        canvas.toBlob((b) => res(b), "image/jpeg", 0.92)
      );

      if (blob && qualityScore > bestScore) {
        bestBlob  = blob;
        bestScore = qualityScore;
      }

      // Stop early if both blur and brightness are acceptable
      if (blurScore >= MIN_BLUR_SCORE && brightnessOk) break;

      if (attempt < maxAttempts - 1) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      }
    }

    if (bestBlob) return bestBlob;
    throw new Error("Failed to capture frame");
  }

  /**
   * Compute average brightness (0–255) of the centre-cropped frame.
   * Used to reject frames that are too dark or overexposed before sending
   * to face comparison — server rejects IMAGE_TOO_DARK below ~40 luminance.
   */
  private _computeBrightness(canvas: HTMLCanvasElement): number {
    const ctx = canvas.getContext("2d");
    if (!ctx) return 128;
    const sw = Math.min(canvas.width, 320);
    const sh = Math.min(canvas.height, 240);
    const sx = Math.floor((canvas.width  - sw) / 2);
    const sy = Math.floor((canvas.height - sh) / 2);
    const { data } = ctx.getImageData(sx, sy, sw, sh);
    let total = 0;
    const pixels = sw * sh;
    for (let i = 0; i < pixels; i++) {
      total += 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
    }
    return total / pixels;
  }

  /**
   * Sum-of-squared Laplacian blur metric on a centre-cropped region (320×240 max).
   * Higher score = sharper image.
   *
   * NOTE: This is sum-of-squares, NOT Laplacian variance (which OpenCV uses).
   * The two scales differ — empirically, a score of ~120 here corresponds to
   * ~80 on the server side (OpenCV cv2.Laplacian().var()). Keep MIN_BLUR_SCORE
   * in captureFrame() calibrated against this.
   */
  private _computeBlurScore(canvas: HTMLCanvasElement): number {
    const ctx = canvas.getContext("2d");
    if (!ctx) return 0;

    // Sample a 320×240 centre crop for performance
    const sw = Math.min(canvas.width, 320);
    const sh = Math.min(canvas.height, 240);
    const sx = Math.floor((canvas.width - sw) / 2);
    const sy = Math.floor((canvas.height - sh) / 2);

    const { data, width, height } = ctx.getImageData(sx, sy, sw, sh);

    // Convert to greyscale
    const gray = new Float32Array(width * height);
    for (let i = 0; i < width * height; i++) {
      gray[i] =
        0.299 * data[i * 4] +
        0.587 * data[i * 4 + 1] +
        0.114 * data[i * 4 + 2];
    }

    // Laplacian convolution [0,1,0 / 1,-4,1 / 0,1,0] — sum of squared responses
    let sum = 0;
    let n = 0;
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const l =
          gray[(y - 1) * width + x] +
          gray[(y + 1) * width + x] +
          gray[y * width + (x - 1)] +
          gray[y * width + (x + 1)] -
          4 * gray[y * width + x];
        sum += l * l;
        n++;
      }
    }
    return n > 0 ? sum / n : 0;
  }
}
